import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TicketJira } from '@vd/types';
import { JiraChip, MenuItem, useToast } from '@vd/ui';
import { api } from '../../lib/api';
import { JiraMenuItem } from './JiraMenuItem';
import styles from './JiraExportControl.module.css';

/**
 * A ticket's Jira state, whether this person may send it, and what can be done
 * with it: one source for the ticket page's three-dots entries, the chip in
 * the line under its title and the note below that, so what one does shows in
 * the others (TOADO-708). Every action is in the three-dots menu, as on a card
 * (Jason, 15 Oct): the page's top row holds no Jira buttons.
 */
function useTicketJira(ticketId: string, projectId: string) {
  const toast = useToast();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['jira-export', ticketId], queryFn: () => api.getJiraExport(ticketId),
    refetchInterval: (state) => state.state.data?.state === 'pending' || state.state.data?.video?.state === 'pending' ? 1500 : false });
  // Whether this person may send, as the board asks it: a viewer sees what
  // was sent, and is not offered what the server would refuse.
  const sendable = useQuery({ queryKey: ['jira-send', projectId], queryFn: () => api.getProjectJiraSend(projectId), staleTime: 60_000 });
  const settle = () => qc.invalidateQueries({ queryKey: ['jira-export', ticketId] });
  // Keyed alike, so one send, unlink or video at a time (Codex review round
  // 18). Settled on the mutation, not the call: the menu closes as it acts,
  // and a call's own callbacks are dropped once its component has gone.
  const mutationKey = ['jira-ticket', ticketId];
  const send = useMutation({
    mutationKey,
    mutationFn: () => api.exportTicketToJira(ticketId),
    onSuccess: (answer) => {
      void settle();
      // What the server did, not what was asked: a ticket already in Jira is left alone.
      toast.push(answer.state === 'exported'
        ? { variant: 'info', title: `Already in Jira as ${answer.issueKey ?? 'an issue'}` }
        : { variant: 'success', title: 'Sending to Jira' });
    },
    onError: (error) => toast.push({ variant: 'danger', title: 'Could not send to Jira', description: (error as Error).message }),
  });
  const unlink = useMutation({
    mutationKey,
    mutationFn: (_issueKey: string) => api.unlinkTicketFromJira(ticketId),
    onSuccess: (_answer, issueKey) => {
      void settle();
      toast.push({ variant: 'success', title: `Unlinked from ${issueKey}`,
        description: 'Jira is not changed. Sending again links this issue back while it is still in Jira, or makes a new one if it was deleted.' });
    },
    onError: (error) => toast.push({ variant: 'danger', title: 'Could not unlink from Jira', description: (error as Error).message }),
  });
  const video = useMutation({
    mutationKey,
    mutationFn: () => api.attachJiraVideo(ticketId),
    onSuccess: () => { void settle(); toast.push({ variant: 'success', title: 'Attaching the walkthrough video' }); },
    onError: (error) => toast.push({ variant: 'danger', title: 'Could not attach the video', description: (error as Error).message }),
  });
  const busy = useIsMutating({ mutationKey }) > 0;
  return { query, canSend: sendable.data?.canSend === true, canUnlink: sendable.data?.canUnlink === true, send, unlink, video, busy };
}

type JiraExport = Awaited<ReturnType<typeof api.getJiraExport>>;

/** The state as a card's chip shows it, or none for a ticket never sent. */
function chipState(state: JiraExport): TicketJira | undefined {
  if (state.state === 'not_exported') return undefined;
  return { state: state.state, issueKey: state.issueKey ?? null, issueUrl: state.issueUrl ?? null,
    error: state.state === 'failed' ? state.error ?? null : null, revision: 0 };
}

/**
 * The ticket page's three-dots entries for Jira: the same as a card's, on this
 * ticket's own state, and attaching its walkthrough video when there is one
 * not yet on the issue (TOADO-704).
 */
export function TicketJiraMenuItem({ ticketId, projectId, onDone }: { ticketId: string; projectId: string; onDone: () => void }) {
  const { query, canSend, canUnlink, send, unlink, video, busy } = useTicketJira(ticketId, projectId);
  const state = query.data;
  // Nothing until the ticket's own state is in: never a send offered for a
  // ticket that may be in Jira already (Codex review round 18).
  if (!state) return null;
  const jira = busy ? { state: 'pending' as const, issueKey: null, issueUrl: null }
    : state.state === 'not_exported' ? null
      : { state: state.state, issueKey: state.issueKey ?? null, issueUrl: state.issueUrl ?? null };
  const attachable = !busy && canSend && state.state === 'exported' && !state.issueMissing
    && (state.video?.state === 'failed' || state.video?.state === 'not_attached');
  return <>
    <JiraMenuItem jira={jira} canSend={canSend} canUnlink={canUnlink} onDone={onDone}
      onSend={() => send.mutate()} onUnlink={() => unlink.mutate(state.issueKey ?? 'the issue')} />
    {attachable && <MenuItem onClick={() => { video.mutate(); onDone(); }}>
      {state.video?.state === 'failed' ? 'Attach the walkthrough video again' : 'Attach the walkthrough video'}
    </MenuItem>}
  </>;
}

/** Where the ticket stands with Jira, in the line under its title: the same chip as its card. */
export function TicketJiraChip({ ticketId, projectId, separator }: { ticketId: string; projectId: string; separator?: React.ReactNode }) {
  const state = useTicketJira(ticketId, projectId).query.data;
  const chip = state && chipState(state);
  return chip ? <>{separator}<JiraChip jira={chip} /></> : null;
}

/**
 * What about the ticket's Jira issue needs a look, on one quiet line under its
 * title, with no buttons: what to do is in the three-dots menu. Nothing when
 * all is well.
 */
export function TicketJiraNotes({ ticketId, projectId }: { ticketId: string; projectId: string }) {
  const { query, canSend, canUnlink } = useTicketJira(ticketId, projectId);
  const state = query.data;
  if (!state) return null;
  const menu = ' From the ⋯ menu, ';
  let note: { text: string; alert: boolean } | null = null;
  // Said as well as shown on the chip, so a screen reader hears the send go
  // (Codex review round 29).
  if (state.state === 'pending') {
    // A send waiting on a create Jira never answered says so, as a check in
    // progress rather than a failure (TOADO-853).
    note = state.error
      ? { text: `Sending to Jira. ${state.error}.`, alert: false }
      : { text: `Sending to Jira${state.attachmentState === 'pending' ? ': uploading images' : ''}…`, alert: false };
  } else if (state.state === 'failed') {
    note = { text: `Not sent to Jira: ${state.error ?? 'an unknown error'}.${canSend ? `${menu}retry the send.` : ''}`, alert: true };
  } else if (state.state === 'exported' && state.issueMissing) {
    // Shown, never acted on: Jira answers the same for a deleted issue and one out of sight (TOADO-712).
    note = { text: `${state.issueKey ?? 'The issue'} was not found in Jira. It may have been deleted, or Toado can no longer see it.${canSend || canUnlink ? `${menu}unlink it.` : ''}`, alert: true };
  } else if (state.state === 'exported' && state.video?.state === 'pending') {
    note = { text: 'Attaching the walkthrough video…', alert: false };
  } else if (state.state === 'exported' && (state.video?.state === 'failed' || state.video?.state === 'not_attached') && state.video.message) {
    note = { text: `${state.video.message}${canSend ? `${menu}attach it again.` : ''}`, alert: false };
  } else if (state.state === 'exported' && state.jiraType?.differs) {
    // A type changed since sending (TOADO-821): Jira refused it, said so, or
    // its change is on its way.
    note = { text: state.jiraType.note ?? `Jira still has this as a ${state.jiraType.inJira}.`, alert: false };
  }
  if (!note) return null;
  return <p className={styles.note} role={note.alert ? 'alert' : 'status'}>{note.text}</p>;
}
